import { useCallback, useEffect, useRef, useState } from 'react';

import { Slider } from '@/components/ui/slider';
import { useVisualization } from '@/context/visualization-context';
import { cn } from '@/lib/utils';
import { CprAngleService } from '@/visualization/services/cpr-angle-service';
import { isCprViewport } from '@/visualization/viewports/viewport-categories';

export interface AngleSliderProps {
    viewportId: string;
}

export function AngleSlider({ viewportId }: AngleSliderProps) {
    const { getViewportLayoutService, getServiceManager } = useVisualization();

    const [angle, setAngle] = useState<number>(0);
    const serviceRef = useRef<CprAngleService | null>(null);

    useEffect(() => {
        const cprAngleService = getServiceManager().getService<CprAngleService>(CprAngleService.SERVICE_NAME);
        serviceRef.current = cprAngleService;
        setAngle(cprAngleService.getCurrentAngle());
        return cprAngleService.subscribe<number>(cprAngleService.EVENTS.ANGLE_CHANGED, (newAngle) => {
            setAngle(newAngle);
        });
    }, [getServiceManager]);

    const handleValueChange = useCallback((values: number[]) => {
        if (values.length === 0) return;
        serviceRef.current?.setCurrentAngle(values[0]);
    }, []);

    const viewport = getViewportLayoutService().getViewport(viewportId);
    if (!isCprViewport(viewport?.type)) return null;

    return (
        <div
            className={cn(
                'absolute top-1 left-2 right-2',
                'flex items-center gap-2',
                'pointer-events-auto',
                'rounded-md shadow-sm',
                'px-2 py-1',
                'opacity-50 hover:opacity-100 transition-opacity duration-300'
            )}
        >
            <Slider
                className={cn(
                    'flex-1',
                    '**:data-[slot=slider-range]:bg-transparent',
                    '**:data-[slot=slider-track]:h-1.5',
                    '**:data-[slot=slider-thumb]:size-4'
                )}
                min={-180}
                max={180}
                value={[angle]}
                onValueChange={handleValueChange}
            />
        </div>
    );
}
